<template>
  <Layout>
    <template v-slot:top_left>
      <AvatarImage
        :img-url="$page.props.shared.wg.icon_url"
        :label="$page.props.shared.wg.name"
        avatar-size="24px"
        img-size="20px"
      />
      <span class="q-ml-sm text-primary text-bold">{{ $page.props.shared.wg.name }}</span>
    </template>

    <!--
    <template v-slot:header>
      <span class="text text-bold">{{ $page.props.shared.wg.name }}</span>
    </template>
    -->

    <template v-slot:default>
      <q-page>
        <div class="centered-container fill-height q-px-xl q-pt-xl">
          <div class="col-12 text-center">
            <!-- Current date -->
            <span class=" text-subtitle1 text-bold">{{ i18n.title_date }}</span>
            <!-- Welcome -->
            <p class="text-flat q-mt-sm text-h4">
              {{ i18n.title_user_name }}
            </p>

            <!-- Empty workspaces message -->
            <p
              v-if="page_title_caption"
              class="text-caption text-grey q-mt-md"
            >
              {{ page_title_caption }}
            </p>
          </div>

          <!-- ---------------- Workspaces ---------------- -->
          <div class="col-12 q-pt-xl">
            <span class="text-h6">{{ i18n.workspaces_title }}</span>
            <q-icon
              name="info_outline"
              class="q-ml-sm q-mb-xs"
              size="xs"
            >
              <q-tooltip
                :delay="500"
                class="q-card shadow text-caption text-default q-pa-md q-menu"
                style="width: 500px"
              >
                {{ i18n.workspaces_description }}
              </q-tooltip>
            </q-icon>
          </div>

          <div
            class="row q-col-gutter-md q-mt-xs justify-start"
          >
            <!-- Create workspace -->
            <div class="col-xs-12 col-sm-4 col-md-3 col-lg-3 self-stretch">
              <q-card
                class="cursor-pointer q-hoverable fit dotted-card text-center card-item"
                @click="workspace_create"
              >
                <span class="q-focus-helper" />
                <q-card-section class="fit items-center">
                  <div class="column items-center justify-center self-center q-pa-sm fit text-center">
                    <q-avatar
                      size="64px"
                      round
                      class="new-item-background"
                    >
                      <!-- + icon -->
                      <q-icon
                        name="las la-plus"
                        size="42px"
                        color="primary"
                      />
                    </q-avatar>
                    <div class="text-body2 q-mt-md q-mb-xs ">
                      {{ i18n.workspaces_create }}
                    </div>
                  </div>
                </q-card-section>
              </q-card>
            </div>

            <!-- Workspace list -->
            <div
              v-for="(workspace) in workspaces"
              :key="workspace.id"
              class="col-xs-12 col-sm-4 col-md-3 col-lg-3 self-stretch"
            >
              <Link :href="workspace.link">
                <q-card
                  class="cursor-pointer q-hoverable text-center fit card-item"
                >
                  <span class="q-focus-helper" />
                  <q-card-section class="fit items-center">
                    <div class="column items-center justify-center self-center q-pa-sm fit text-center">
                      <q-avatar
                        size="48px"
                        square
                      >
                        <Jdenticon
                          :text="workspace.id"
                          :size="48"
                        />
                      </q-avatar>
                      <div class="text-subtitle2 q-mt-md q-mb-none">
                        {{ workspace.name }}
                      </div>
                      <p class="text-caption q-mt-none q-mb-none">
                        {{ workspace.professional ? i18n.workspaces_list_business : i18n.workspaces_list_individual }}
                      </p>
                    </div>
                  </q-card-section>
                </q-card>
              </Link>
            </div>
          </div>
          <div
            v-if="wg_admin_link"
            class="row justify-center"
          >
            <div
              class="col-xs-12 col-md-6 text-center"
            >
              <!-- Banner with link to WorkspaceGroup Admin -->
              <q-banner
                class="bg-pastel-blue q-pa-md q-mt-lg"
                dense
              >
                <template v-slot:avatar>
                  <q-icon
                    name="las la-info-circle"
                    class=""
                    size="lg"
                  />
                </template>
                <div
                  class="text-caption text-dark"
                >
                  {{ i18n.workspaces_admin_banner }}
                  <Link
                    :href="wg_admin_link"
                    class="underline"
                  >
                    {{ i18n.workspaces_admin_banner_link_label }}
                  </Link>
                </div>
              </q-banner>
            </div>
          </div>

          <!-- Invitations -->
          <div class="col-12 q-mt-xl q-pt-xl">
            <span class="text-h6">{{ i18n.invitations_title }}</span>
          </div>

          <!-- No invitations -->
          <q-banner
            v-if="invitations.length == 0"
            class="bg-pastel-blue q-px-md q-mt-lg"
            dense
            style="width: fit-content"
          >
            <template v-slot:avatar>
              <q-icon
                name="las la-info-circle"
                class=""
                size="sm"
              />
            </template>
            <div class="">
              {{ i18n.invitations_list_empty }}
            </div>
          </q-banner>

          <div class="row q-col-gutter-md q-mt-xs justify-start">
            <div
              v-for="(invitation) in invitations"
              :key="invitation.id"
              class="col-xs-12 col-sm-12 col-md-6 col-lg-6 self-stretch"
            >
              <q-card class="text-center fit">
                <q-card-section class="fit items-center">
                  <div class="column items-center justify-center self-center q-pa-sm fit text-center">
                    <q-item
                      class="text-left"
                      dense
                    >
                      <q-item-section avatar>
                        <q-avatar
                          square
                          color="white"
                          size="48px"
                        >
                          <Jdenticon
                            :text="invitation.workspace_id"
                            :size="48"
                          />
                        </q-avatar>
                      </q-item-section>

                      <q-item-section>
                        <q-item-label class="text-bold">
                          {{ invitation.workspace_name }}
                        </q-item-label>
                        <q-item-label
                          v-if="invitation.workspace_siren"
                          class="text-grey-6 text-caption"
                        >
                          SIREN: {{ invitation.workspace_siren }}
                        </q-item-label>
                      </q-item-section>
                    </q-item>

                    <q-separator
                      inset
                      class="q-mx-xl"
                    />
                    <q-card-section class="text-center q-my-ms q-mx-sm">
                      <div class="text-caption">
                        <q-avatar
                          size="xs"
                          class="q-mr-sm"
                          style="margin-bottom: 2px"
                        >
                          <img
                            :src="invitation.user_avatar_url"
                            referrerpolicy="no-referrer"
                          >
                        </q-avatar>
                        {{ invitation.user_name }} {{ i18n.invitations_user_invite_you }}
                      </div>
                    </q-card-section>
                    <q-card-section class="text-center q-pa-sm q-mx-none full-width">
                      <!-- Accept and decline buttons -->
                      <div class="row no-wrap justify-around">
                        <q-btn
                          unelevated
                          rounded
                          color="negative"
                          size="sm"
                          class="text-white q-px-lg"
                          :label="i18n.invitations_action_decline"
                          no-caps
                          @click="invitations_respond(invitation.action_reject_link)"
                        />

                        <q-btn
                          color="positive"
                          class="q-px-lg"
                          size="sm"
                          unelevated
                          rounded
                          no-caps
                          :label="i18n.invitations_action_accept"
                          @click="invitations_respond(invitation.action_accept_link)"
                        />
                      </div>
                    </q-card-section>
                  </div>
                </q-card-section>
              </q-card>
            </div>
          </div>
        </div>

        <q-dialog v-model="workspaces_dialog_new_workspace_blocked_show">
          <q-card
            class="q-pa-xl"
            style="width: 700px; max-width: 80vw"
          >
            <div class="row text-center">
              <div class="col-12 text-center">
                <q-avatar size="56px">
                  <q-img
                    :src="
                      $q.dark.isActive
                        ? $page.props.shared.wg.logo_dark_url
                        : $page.props.shared.wg.logo_light_url
                    "
                  />
                </q-avatar>
              </div>
              <div class="col-12 q-pt-lg">
                <span class="text-h6">
                  {{ i18n.workspaces_no_create_dialog_title }}
                </span>

                <p class="q-pt-sm">
                  {{ i18n.workspaces_no_create_dialog_description }}<br>{{ i18n.workspaces_no_create_dialog_contact }}
                </p>

                <div class="col-12 q-mt-lg">
                  <q-btn
                    :label="$q.lang.label.cancel"
                    flat
                    size="sm"
                    @click="workspaces_dialog_new_workspace_blocked_show = false"
                  />
                </div>
              </div>
            </div>
          </q-card>
        </q-dialog>
      </q-page>
    </template>
  </Layout>
</template>
<script>
import AvatarImage from '@/shared/avatar_image.vue';
import Jdenticon from '@/shared/jdenticon.vue';
import Layout from '@/layouts/wide.vue';
import { Link } from '@inertiajs/vue3';

export default {
  components: {
    AvatarImage,
    Jdenticon,
    Layout,
    Link,
  },
  props: {
    i18n: {
      type: Object,
      required: true,
    },
    page_title_caption: {
      type: String,
      required: true,
    },
    wg_admin_link: {
      type: String,
      required: false,
    },
    workspaces_new_allowed: {
      type: Boolean,
      required: true,
    },
    workspaces_new_link: {
      type: String,
      required: true,
    },
    workspaces: {
      type: Array,
      required: true,
    },
    invitations: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      workspaces_dialog_new_workspace_blocked_show: false,
    };
  },
  computed: {
  },
  watch: {
  },
  methods: {
    workspace_create: function () {
      if (!this.workspaces_new_allowed) {
        this.workspaces_dialog_new_workspace_blocked_show = true;
        return;
      }
      this.$inertia.get(this.workspaces_new_link);
    },
    invitations_respond: function (link) {
      this.$inertia.put(
        link,
        {},
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.log(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
          },
        }
      );
    },
    submit() {
      this.form.put(this.$page.props.form_action, {
        preserveScroll: true,
        onError: (e) => {
          console.log(e);
          this.$q.notify({
            message: this.$page.props.flash.alert,
            type: "negative",
          });
        },
        onSuccess: () => {
          this.$q.notify({
            message: this.$page.props.flash.success,
            type: "positive",
          });
        },
      });
    },
    string_len_rule(string) {
      if (!string) {
        return this.$page.props.i18n.required;
      } else if (string.length > 40) {
        return this.$page.props.i18n.limit_max_char_40;
      } else {
        return true;
      }
    },
    phone_number_validation({ valid }) {
      if (!valid) {
        this.phone_error = this.$page.props.i18n.invalid_phone_number;
      } else {
        this.phone_error = null;
      }
    }
  },
};
</script>
